import React from "react";
import { toast } from "react-toastify";

import {
  KintoneExportErrorDetail,
  KintoneExportReport,
  KintoneExportSetting,
  nullToUndefinedDeep,
} from "@graffer-inc/cloud-gov-core";
import {
  gqlClient,
  reportUnexpectedError,
  runGqlQueryV2,
} from "@graffer-inc/smart-apply";

import type { ManualExportSubmissionFuncType } from "../../../../../organisms/kintone-export/common-hook";

type Props = {
  kintoneExportSettingId: string;
  kintoneExportReportId: string;
};

type PageContent = {
  kintoneExportReport: KintoneExportReport;
  kintoneExportSetting: KintoneExportSetting;
};

export const useKintoneExportReportDetailPage = ({
  kintoneExportSettingId,
  kintoneExportReportId,
}: Props) => {
  const [pageContent, setPageContent] = React.useState<PageContent>();

  React.useEffect(() => {
    const fetchKintoneExportReport = async () => {
      try {
        const res = await runGqlQueryV2(() => {
          return gqlClient.kintoneExportReport({
            kintoneExportSettingId,
            id: kintoneExportReportId,
          });
        });

        if (res.error) {
          reportUnexpectedError(res.error?.rawError);
          toast.error("連携実行記録の取得に失敗しました。");
          return;
        }

        setPageContent({
          kintoneExportReport: nullToUndefinedDeep(
            res.data!.kintoneExportReport,
          ),
          kintoneExportSetting: nullToUndefinedDeep(
            res.data!.kintoneExportSetting,
          ),
        });
      } catch (error) {
        reportUnexpectedError(error);
        toast.error("連携実行記録の取得に失敗しました。");
      }
    };
    fetchKintoneExportReport();
  }, [kintoneExportSettingId, kintoneExportReportId, setPageContent]);

  return {
    pageContent,
  };
};

export const useKintoneExportErrorDetails = ({
  kintoneExportReportId,
}: {
  kintoneExportReportId: string;
}) => {
  const [kintoneExportErrorDetails, setKintoneExportErrorDetails] =
    React.useState<KintoneExportErrorDetail[]>([]);

  React.useEffect(() => {
    const fetchKintoneExportErrorDetails = async () => {
      try {
        const res = await runGqlQueryV2(() => {
          return gqlClient.kintoneExportErrorDetails({
            reportId: kintoneExportReportId,
          });
        });

        if (res.error) {
          reportUnexpectedError(res.error?.rawError);
          toast.error("連携実行エラー詳細の取得に失敗しました。");
          return;
        }

        setKintoneExportErrorDetails(
          res.data!.kintoneExportErrorDetails.edges.map((edge) => edge.node),
        );
      } catch (error) {
        reportUnexpectedError(error);
        toast.error("連携実行エラー詳細の取得に失敗しました。");
      }
    };
    fetchKintoneExportErrorDetails();
  }, [kintoneExportReportId]);

  return {
    kintoneExportErrorDetails,
  };
};

interface UseReExportErrorSubmissionProps {
  manualExportSubmission: ManualExportSubmissionFuncType;
  moveToReportDetail: (reportId: KintoneExportReport["id"]) => void;
}

export const useReExportErrorSubmission = ({
  manualExportSubmission,
  moveToReportDetail,
}: UseReExportErrorSubmissionProps) => {
  const [isReExporting, setIsReExporting] = React.useState(false);
  const reExportErrorSubmission = async (
    kintoneExportSettingId: string,
    submissionIds: string[],
  ) => {
    setIsReExporting(true);

    manualExportSubmission(
      { submissionIds, settingId: kintoneExportSettingId },
      {
        onSucceeded: (report) => {
          toast.success("連携しました。");
          moveToReportDetail(report.id);
          setIsReExporting(false);
        },
        onFailed: (errorMessage) => {
          toast.error(errorMessage);
          setIsReExporting(false);
        },
        onPolingTimeout: () => {
          toast.info(
            "連携処理は継続中です。連携完了後実行結果は連携実行記録から確認できます。",
            {
              autoClose: false,
            },
          );
          setIsReExporting(false);
        },
      },
    );
  };

  return {
    isReExporting,
    reExportErrorSubmission,
  };
};
